import { z } from 'zod'
import { outlineCrossesItself } from '../helpers/furniture-floor.js'

/**
 * A furniture area: a named outline on a room's drawing, inside which furniture
 * stands. A room may have any number; touching areas make one floor. A room with
 * none has no rule at all. See `docs/furniture-areas.md`.
 *
 * Kept apart from the zones on purpose. A zone is paint — its type is a preset
 * that recolors it — and an area is meaning with no paint, so choosing a preset
 * can never change where furniture may go.
 *
 * `points` is the closed outline, `[x0, y0, x1, y1, …]` in the room's design
 * pixels (an inch each).
 */

/** The longest an area's name may be. */
export const FURNITURE_AREA_NAME_MAX = 60

export const furnitureAreaSchema = z.object({
  id: z.string().min(1),
  /** What people, and an assistant, call it: "Tent", "Cocktail lawn". */
  name: z.string().trim().min(1, 'Give the area a name.').max(FURNITURE_AREA_NAME_MAX),
  points: z
    .array(z.number())
    .min(6, 'An area needs at least three corners.')
    .refine((points) => points.length % 2 === 0, 'Every corner needs both coordinates.'),
})

export type FurnitureArea = z.infer<typeof furnitureAreaSchema>

/**
 * A room's areas as stored and sent. Read as they are, without the checks a save
 * makes: an area that would no longer pass them must still load, rather than
 * take its room with it.
 */
export const storedFurnitureAreasSchema = z.array(furnitureAreaSchema)

/**
 * A room's areas, as a save sends them. Names are unique within a room, ignoring
 * case, because areas are referred to by name, and no outline crosses itself,
 * because one that does has no clear inside: two ways of asking whether a piece
 * is in it would answer differently.
 */
export const furnitureAreasSchema = z.array(furnitureAreaSchema).superRefine((areas, ctx) => {
  const problem = namedOutlinesProblem(areas, 'areas')
  if (problem !== null) ctx.addIssue({ code: 'custom', message: problem })
})

/**
 * What two names of a room's outlines are compared by: the same name, whatever
 * its case and the spaces round it, is the same name. One rule for the editor's
 * refusals, the save's and the API's, so none accepts a name another refuses.
 */
export function outlineNameKey(name: string): string {
  return name.trim().toLowerCase()
}

/**
 * The first reason a room's named outlines cannot be saved, or null when they
 * can: two with one name, ignoring case, or an outline that crosses itself.
 * Shared with landmarks (`landmark.dto.ts`), which are named and drawn the same
 * way. `plural` names them in the message: "Two areas are called …".
 */
export function namedOutlinesProblem(
  outlines: ReadonlyArray<{ name: string; points: readonly number[] }>,
  plural: string,
): string | null {
  const seen = new Set<string>()
  for (const outline of outlines) {
    const key = outlineNameKey(outline.name)
    if (seen.has(key)) return `Two ${plural} are called “${outline.name.trim()}”.`
    seen.add(key)
    if (outlineCrossesItself(outline.points)) return `The outline of “${outline.name.trim()}” crosses itself.`
  }
  return null
}
